import { UserErrorIcon, UserLoadingIcon } from '../../../assets/icons/user';
import { phiProps } from '../../../lib/phi';
import { cn } from '../../../lib/utils';

const PATIENT_INITIALS_FALLBACK = '?';

interface BaseProfilePictureProps {
  /** Size in pixels (default: 64) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Loading state profile picture - shows loading icon
 * Used in skeleton states (from Figma: 10322-63682)
 */
export function LoadingProfilePicture({
  size = 64,
  className,
}: BaseProfilePictureProps) {
  return (
    <div
      className={cn('shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <div
        className="bg-gray-50 rounded-lg flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <UserLoadingIcon width={size * 0.375} height={size * 0.375} />
      </div>
    </div>
  );
}

/**
 * Error state profile picture - shows error icon
 * Used when there's an error loading patient data (from Figma: 10322-65271)
 */
export function ErrorProfilePicture({
  size = 64,
  className,
}: BaseProfilePictureProps) {
  return (
    <div
      className={cn('shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <div
        className="bg-gray-50 rounded-lg flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <UserErrorIcon width={size * 0.375} height={size * 0.375} />
      </div>
    </div>
  );
}

export interface PatientProfilePictureProps {
  /** The image URL to display */
  src?: string | null | undefined;
  /** The patient name (used for initials fallback) */
  name: string;
  /** Size in pixels (default: 64) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Whether the patient data is loading */
  isLoading?: boolean;
  /** Whether there was an error loading patient data */
  isError?: boolean;
}

function getPatientInitials(name: string): string {
  const nameParts = name.trim().split(/\s+/).filter(Boolean);

  if (nameParts.length === 0) {
    return PATIENT_INITIALS_FALLBACK;
  }

  const firstInitial = nameParts[0]?.charAt(0) ?? '';
  const lastInitial =
    nameParts.length > 1
      ? (nameParts[nameParts.length - 1]?.charAt(0) ?? '')
      : '';

  return `${firstInitial}${lastInitial}`.toUpperCase();
}

/**
 * Main profile picture component with loading/error state handling
 */
export function PatientProfilePicture({
  src,
  name,
  size = 64,
  className,
  isLoading = false,
  isError = false,
}: Readonly<PatientProfilePictureProps>) {
  // If there's an error, show error icon
  if (isError) {
    return <ErrorProfilePicture size={size} className={className} />;
  }

  // If data is loading, show loading icon
  if (isLoading) {
    return <LoadingProfilePicture size={size} className={className} />;
  }

  const initials = getPatientInitials(name);

  // If no src provided, show initials (from Figma: 10403-65323)
  if (!src) {
    return (
      <div
        className={cn('shrink-0', className)}
        style={{ width: size, height: size }}
      >
        <div
          className="flex size-full items-center justify-center rounded-lg border border-primary-100 bg-primary-50 font-sans font-semibold text-primary-700 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-300"
          style={{
            fontSize: size * 0.3125, // 20px for 64px avatar
            lineHeight: 1,
          }}
          {...phiProps()}
        >
          {initials}
        </div>
      </div>
    );
  }

  // Success: show image
  return (
    <div
      className={cn('shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <img
        src={src}
        alt={`${name} profile`}
        className="w-full h-full object-cover rounded-lg"
        {...phiProps()}
      />
    </div>
  );
}
